import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  IAdminBooking,
  IAdminSlot,
  IBulkCreateSlotsRequest,
  IBulkCreateSlotsResponse,
  ICancelBookingRequest,
  IConfirmMeetingRequest,
  IRescheduleBookingRequest,
  IUpdateSlotRequest,
  ConsultationBookingStatus,
} from '../../../assets/types/dtoTypes';

/**
 * Client for the admin consultation endpoints (backend PRs #67 & #68):
 * booking worklist + lifecycle actions and slot management.
 */
@Injectable({ providedIn: 'root' })
export class AdminConsultationService {
  private http = inject(HttpClient);

  private url = `${environment.apiUrl}/admin/consultation`;

  // ----- bookings ---------------------------------------------------------

  listBookings(status?: ConsultationBookingStatus): Observable<IAdminBooking[]> {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    return this.http.get<IAdminBooking[]>(`${this.url}/bookings`, { params });
  }

  confirmMeeting(bookingId: string, body: IConfirmMeetingRequest): Observable<IAdminBooking> {
    return this.http.post<IAdminBooking>(`${this.url}/bookings/${bookingId}/confirm`, body);
  }

  rescheduleBooking(bookingId: string, body: IRescheduleBookingRequest): Observable<IAdminBooking> {
    return this.http.post<IAdminBooking>(`${this.url}/bookings/${bookingId}/reschedule`, body);
  }

  cancelBooking(bookingId: string, body: ICancelBookingRequest): Observable<IAdminBooking> {
    return this.http.post<IAdminBooking>(`${this.url}/bookings/${bookingId}/cancel`, body);
  }

  completeBooking(bookingId: string): Observable<IAdminBooking> {
    return this.http.post<IAdminBooking>(`${this.url}/bookings/${bookingId}/complete`, {});
  }

  confirmPayment(paymentId: string): Observable<IAdminBooking> {
    return this.http.post<IAdminBooking>(`${this.url}/payments/${paymentId}/confirm`, {});
  }

  rejectPayment(paymentId: string): Observable<IAdminBooking> {
    return this.http.post<IAdminBooking>(`${this.url}/payments/${paymentId}/reject`, {});
  }

  // ----- slots ------------------------------------------------------------

  listSlots(from: Date, to: Date): Observable<IAdminSlot[]> {
    const params = new HttpParams()
      .set('from', from.toISOString())
      .set('to', to.toISOString());
    return this.http.get<IAdminSlot[]>(`${this.url}/slots`, { params });
  }

  bulkCreateSlots(body: IBulkCreateSlotsRequest): Observable<IBulkCreateSlotsResponse> {
    return this.http.post<IBulkCreateSlotsResponse>(`${this.url}/slots/bulk`, body);
  }

  updateSlot(slotId: string, body: IUpdateSlotRequest): Observable<IAdminSlot> {
    return this.http.patch<IAdminSlot>(`${this.url}/slots/${slotId}`, body);
  }

  deleteSlot(slotId: string): Observable<void> {
    return this.http.delete<void>(`${this.url}/slots/${slotId}`);
  }
}
